import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Download, TrendingUp, TrendingDown, BookOpen, Sparkles, ArrowRight, Zap, Activity, AlertTriangle, ShieldCheck } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import BrandHeader from "@/components/BrandHeader";
import { TESTIDS } from "@/constants/testIds";
import { formatDate, levelBadgeClass } from "@/lib/format";
import { renderRich } from "@/lib/renderRich";
import { CadenaBlock } from "@/components/cadena/CadenaBlock";

const AREA_LABEL = {
  A: "Marca empleadora",
  B: "Selección",
  C: "Onboarding",
  D: "Evaluación",
  E: "Feedback",
  F: "Desarrollo",
};

// Paleta hasta 5 series (mismo criterio que ConsultingCompare)
const SERIES_COLORS = ["#9ca3af", "#f59e0b", "#0891b2", "#7c3aed", "#0f6e56"];

const INSIGHT_ICONS = {
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  zap: Zap,
  "alert-triangle": AlertTriangle,
  "shield-check": ShieldCheck,
  activity: Activity,
};

export default function Compare() {
  // Ruta legacy: /compare/:a/:b(/:c). Colecta hasta 3 desde params, luego
  // fetch al endpoint POST /diagnostic/compare que soporta hasta 5 y devuelve
  // el payload enriquecido (timeline, top_changes, micro_insights, storyline).
  const { a, b, c } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ids = useMemo(() => {
    const qsIds = (searchParams.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (qsIds.length >= 2) return qsIds.slice(0, 5);
    return [a, b, c].filter(Boolean);
  }, [a, b, c, searchParams]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      if (ids.length < 2) {
        setError("Se necesitan al menos 2 diagnósticos para comparar.");
        return;
      }
      try {
        const r = await api.post("/diagnostic/compare", { ids });
        setData(r.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || "No pudimos cargar la comparativa";
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [ids.join(",")]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <BrandHeader right={<button className="btn-ghost" onClick={() => navigate("/dashboard")}><ArrowLeft className="w-4 h-4" /> Volver</button>} />
        <div className="max-w-3xl mx-auto p-12 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <BrandHeader />
        <div className="max-w-3xl mx-auto p-12 text-gray-500 inline-flex items-center gap-2">
          <Loader2 className="animate-spin w-4 h-4" /> Preparando el análisis comparativo…
        </div>
      </div>
    );
  }

  const { summaries, chrono, storyline, top_changes, timeline_points, micro_insights, areas } = data;
  const n = summaries.length;
  const first = chrono[0];
  const last = chrono[chrono.length - 1];
  const delta = last.overall_score - first.overall_score;
  const deltaPct = last.overall_pct - first.overall_pct;

  async function handleDownloadPdf() {
    setDownloading(true);
    try {
      await downloadPdfWithProgress({
        url: `/diagnostic/compare/pdf?ids=${ids.join(",")}`,
        filename: `Comparativa_${n}_diagnosticos.pdf`,
        kind: "std",
        label: "Preparando la comparativa",
      });
    } catch (err) {
      toast.error(err?.response?.status === 400 ? "Solo puedes descargar comparativas entre 2 y 5 diagnósticos." : "No pudimos generar el PDF");
    } finally {
      setDownloading(false);
    }
  }

  const radarData = ["A", "B", "C", "D", "E", "F"].map((code) => {
    const row = { area: code, label: AREA_LABEL[code] };
    summaries.forEach((s, i) => { row[`s${i}`] = s.per_area[code]?.score || 0; });
    return row;
  });

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid={TESTIDS.compare.root}>
      <BrandHeader right={<button className="btn-ghost" onClick={() => navigate("/dashboard")} data-testid="compare-back-btn"><ArrowLeft className="w-4 h-4" /> Volver</button>} />
      <main className="max-w-5xl mx-auto px-4 md:px-10 py-8 md:py-14">
        {/* Header + botón PDF */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-2">Comparativa entre diagnósticos</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900">Evolución del talento · {n} diagnósticos</h1>
          </div>
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white transition disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", boxShadow: "0 10px 24px -12px rgba(11,61,46,0.4)" }}
            data-testid="compare-download-pdf"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? "Generando…" : "Descargar PDF"}
          </button>
        </div>

        {/* Storyline LLM */}
        {(storyline?.storyline || storyline?.titulo) && (
          <div
            className="relative rounded-3xl p-6 md:p-8 mb-6 overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)", boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset" }}
            data-testid="compare-storyline"
          >
            <span aria-hidden className="absolute -top-8 -right-8 pointer-events-none" style={{ width: 220, height: 220, background: "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%),repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)" }} />
            <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                <BookOpen className="w-3.5 h-3.5" /> Trazo narrativo
              </div>
              {storyline.titulo && <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight mb-4">{renderRich(storyline.titulo)}</h2>}
              {storyline.storyline && <p className="text-white/90 leading-relaxed text-[15px] md:text-base max-w-3xl">{renderRich(storyline.storyline)}</p>}
              {storyline.highlight && (
                <div className="mt-5 inline-flex items-start gap-2 rounded-full px-4 py-2" style={{ background: "rgba(201,168,76,0.18)", border: "1px solid rgba(201,168,76,0.4)" }}>
                  <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#E5C874" }} />
                  <span className="text-sm text-white/95 font-medium">{renderRich(storyline.highlight)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cards por diagnóstico */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-2 mb-6">
          {summaries.map((s, i) => (
            <div className="rounded-2xl border border-gray-100 bg-white p-4" key={s.id} data-testid={`compare-diag-card-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-gray-400">Test {i + 1}</div>
              <div className="font-display text-sm font-bold text-gray-900 mt-1">{formatDate(s.created_at)}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-2xl font-display font-bold" style={{ color: SERIES_COLORS[i] }}>{s.overall_score}</span>
                <span className="text-xs text-gray-400">/120</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5"><strong style={{ color: "#0F6E56" }}>{s.overall_pct}</strong>/100</div>
              <span className={`${levelBadgeClass(s.overall_level)} mt-2`}>{s.overall_level}</span>
            </div>
          ))}
        </div>

        {/* Timeline SVG */}
        {timeline_points && timeline_points.length >= 2 && (
          <div className="bg-white rounded-2xl shadow p-5 md:p-6 mb-6" data-testid="compare-timeline">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-400">Línea de tiempo · índice global (0-100)</div>
              <span className={`text-sm font-bold ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="compare-delta">
                {delta >= 0 ? "+" : ""}{delta} pts · {deltaPct >= 0 ? "+" : ""}{deltaPct}/100
              </span>
            </div>
            <TimelineSVG points={timeline_points} />
            <div className="mt-3 text-[11px] text-slate-500">Eje vertical: puntuación global en escala 0-100. Puntos ordenados cronológicamente.</div>
          </div>
        )}

        {/* Micro-insights automáticos */}
        {micro_insights && micro_insights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="compare-micro-insights">
            {micro_insights.map((ins, i) => {
              const Icon = INSIGHT_ICONS[ins.icon] || Sparkles;
              return (
                <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0" style={{ background: "rgba(15,110,86,0.08)", color: "#0F6E56" }}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <p className="text-[13.5px] text-slate-700 leading-snug">{renderRich(ins.text)}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Top changes */}
        {top_changes && (top_changes.positivas?.length > 0 || top_changes.negativas?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <TopChangesCard title="Áreas de mayor mejora" rows={top_changes.positivas || []} icon={<TrendingUp className="w-4 h-4" />} color="#0F6E56" bg="#ecfdf5" testid="compare-top-pos" />
            <TopChangesCard title="Áreas de mayor deterioro" rows={top_changes.negativas || []} icon={<TrendingDown className="w-4 h-4" />} color="#b91c1c" bg="#fef2f2" testid="compare-top-neg" />
          </div>
        )}

        {/* Radar o cadena del talento (interruptor) */}
        {data.cadena ? (
        <div className="card mt-2" data-testid="compare-cadena">
          <CadenaBlock cadena={data.cadena} cambios={data.cadena_cambios} />
        </div>
        ) : (
        <div className="card mt-2">
          <h3 className="font-display text-lg font-bold text-gray-900 mb-1">Radar comparativo</h3>
          <p className="text-xs text-gray-500 mb-4">Puntos por área sobre 20.</p>
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="area" tick={{ fill: "#4b5563", fontSize: 13, fontWeight: 600 }} />
                <PolarRadiusAxis angle={90} domain={[0, 20]} tick={{ fill: "#9ca3af", fontSize: 10 }} />
                {summaries.map((_, i) => (
                  <Radar key={i} name={`Test ${i + 1}`} dataKey={`s${i}`} stroke={SERIES_COLORS[i]} fill={SERIES_COLORS[i]} fillOpacity={0.22} strokeWidth={2} />
                ))}
                <Legend />
                <Tooltip formatter={(v) => `${v}/20`} contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}

        {/* Detalle por área */}
        <div className="mt-8 space-y-3">
          {areas.map((a) => {
            const values = summaries.map((s) => s.per_area[a.code]?.score || 0);
            const d = values[values.length - 1] - values[0];
            return (
              <div key={a.code} className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-4 flex-wrap" data-testid={`compare-area-row-${a.code}`}>
                <div className="w-10 h-10 rounded-xl bg-[#0f6e56]/8 text-[#0f6e56] font-display font-bold flex items-center justify-center flex-shrink-0">{a.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm">{a.name}</div>
                </div>
                <div className="flex items-center gap-4 md:gap-6 text-right flex-wrap justify-end">
                  {values.map((v, i) => (
                    <div key={i}>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: SERIES_COLORS[i] }}>Test {i + 1}</div>
                      <div className="text-base font-display font-semibold text-gray-700">{v}<span className="text-xs text-gray-400">/20</span></div>
                    </div>
                  ))}
                  <div className={`text-sm font-bold min-w-[48px] ${d >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{d >= 0 ? "+" : ""}{d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function TopChangesCard({ title, rows, icon, color, bg, testid }) {
  return (
    <div className="rounded-2xl p-5 border" style={{ background: bg, borderColor: `${color}33` }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3" style={{ color }}>{icon}<div className="text-xs uppercase tracking-widest font-bold">{title}</div></div>
      {rows.length ? (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.code} className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold flex-shrink-0" style={{ background: color, color: "#fff" }}>{r.code}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate">{r.name}</div>
                <div className="text-xs text-slate-600 flex items-center gap-1.5">
                  <span>{r.from}/20</span><ArrowRight className="w-3 h-3 text-slate-400" /><span>{r.to}/20</span>
                  <span className="text-slate-400 ml-1">({r.delta > 0 ? "+" : ""}{r.delta})</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="text-xs text-slate-400 italic">Sin variaciones destacadas.</p>}
    </div>
  );
}

function TimelineSVG({ points }) {
  const W = 720, H = 200, PAD_L = 44, PAD_R = 24, PAD_T = 24, PAD_B = 44;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = points.length;
  const xAt = (i) => PAD_L + (n === 1 ? innerW / 2 : (innerW * i) / (n - 1));
  const yAt = (v) => PAD_T + innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;
  const val = (p) => (typeof p.pct === "number" ? p.pct : 0);
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(val(p)).toFixed(1)}`).join(" ");
  const gridLevels = [0, 25, 50, 75, 100];
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" className="min-w-[520px]">
        <rect x={PAD_L} y={PAD_T} width={innerW} height={innerH} fill="#fafaf7" rx={12} />
        {gridLevels.map((lvl) => (
          <g key={lvl}>
            <line x1={PAD_L} x2={PAD_L + innerW} y1={yAt(lvl)} y2={yAt(lvl)} stroke="#e2e8f0" strokeDasharray="3 4" strokeWidth={0.8} />
            <text x={PAD_L - 8} y={yAt(lvl) + 4} textAnchor="end" fontSize="10" fill="#94a3b8" fontWeight="600">{lvl}</text>
          </g>
        ))}
        <defs>
          <linearGradient id="tl-diag-line" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stopColor="#0F6E56" /><stop offset="100%" stopColor="#C9A84C" /></linearGradient>
          <linearGradient id="tl-diag-area" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#0F6E56" stopOpacity="0.22" /><stop offset="100%" stopColor="#0F6E56" stopOpacity="0" /></linearGradient>
        </defs>
        {n > 1 && <path d={`${pathD} L ${xAt(n - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xAt(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`} fill="url(#tl-diag-area)" />}
        {n > 1 && <path d={pathD} fill="none" stroke="url(#tl-diag-line)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />}
        {points.map((p, i) => {
          const cx = xAt(i), cy = yAt(val(p));
          const dateShort = p.date ? new Date(p.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "";
          return (
            <g key={p.id}>
              <circle cx={cx} cy={cy} r={6.5} fill="#fff" stroke="#0F6E56" strokeWidth={2.5} />
              <circle cx={cx} cy={cy} r={2.8} fill="#C9A84C" />
              <text x={cx} y={cy - 12} textAnchor="middle" fontSize="11" fontWeight="700" fill="#0b3d2e">{val(p)}</text>
              <text x={cx} y={PAD_T + innerH + 18} textAnchor="middle" fontSize="10" fontWeight="600" fill="#334155">Test {i + 1}</text>
              {dateShort && <text x={cx} y={PAD_T + innerH + 32} textAnchor="middle" fontSize="9" fill="#94a3b8">{dateShort}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
